import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  CommissionEstimateDashboard,
  CommissionEstimateLocation,
  CommissionEstimateSources,
  CommissionEstimateSummary
} from '@app/shared/models/commission-estimate-report.model';
import { SelectedRange } from '@app/shared/models/volume-dashboard.model';

interface CommissionSourceRow {
  key: keyof CommissionEstimateSources;
  label: string;
  value: number;
  width: number;
}

@Component({
  selector: 'app-commission-estimate-report',
  templateUrl: './commission-estimate-report.component.html',
  styleUrl: './commission-estimate-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommissionEstimateReportComponent {
  @Input() report: CommissionEstimateSummary | CommissionEstimateDashboard | null = null;
  @Input() selectedRange: SelectedRange | null = null;
  @Input() isLoading = false;
  @Input() showLocations = false;
  @Input() compact = false;
  @Input() usePayoutTerminology = false;

  // A zero total uses the same empty state as the other reporting cards.
  get hasData(): boolean {
    return (this.report?.totals.postTax.value ?? 0) > 0;
  }

  get locations(): CommissionEstimateLocation[] {
    return 'locations' in (this.report ?? {})
      ? (this.report as CommissionEstimateDashboard).locations
      : [];
  }

  get hasPercent(): boolean {
    return this.report?.totals.postTax.diffPercent != null;
  }

  get comparisonValue(): number | null {
    if (!this.report) return null;
    // When the prior total is zero, show the absolute change instead of an undefined percentage.
    return this.hasPercent
      ? this.report.totals.postTax.diffPercent
      : this.report.totals.postTax.diffAbsolute;
  }

  get sourceRows(): CommissionSourceRow[] {
    if (!this.report) return [];
    const sources = this.report.sources;
    const rows = [
      { key: 'dropOff' as const, label: 'App.Charts.dropOff', value: sources.dropOff },
      { key: 'hal' as const, label: 'App.Charts.hal', value: sources.hal },
      { key: 'subscription' as const, label: 'App.Charts.subscription', value: sources.subscription },
      { key: 'outbound' as const, label: 'App.Charts.outbound', value: sources.outbound },
    ];
    // Source amounts are pre-tax, so their bar widths use the pre-tax total.
    const total = this.report.totals.preTax;
    return rows.map((row) => ({ ...row, width: total > 0 ? row.value / total * 100 : 0 }));
  }
}
